import { plain } from '@app/stationery'
import {
  isNoteLine,
  labelWithPlace,
  scheduledDays,
  sheetTitleHe,
  withSunset,
} from './layout'
import { type ScheduleSegmentKey, SEGMENT_LABEL, segmentDays } from './segments'
import type { LuachDay, LuachEntry, LuachResult, LuachShul } from './types'

/**
 * Isolate a run of text from the bidirectional algorithm around it.
 *
 * A line like `שחרית: 8:45 AM` is stored in the right order and still renders
 * as `8:45 AM שחרית:`. Nothing is corrupt: the colon, the digits and the dashes
 * are BIDI-neutral, so the algorithm resolves their direction from whatever
 * surrounds them, and a Hebrew label pulls them into its own run. Every mail
 * client and browser does this, correctly, because we never said where one run
 * ends and the next begins.
 *
 * FSI…PDI (U+2068…U+2069) says exactly that. "First strong isolate" takes its
 * direction from the first strong character INSIDE it, so one helper serves a
 * Hebrew label and a Latin time without either being classified here — and the
 * text inside can no longer reorder the text outside.
 *
 * Chosen over RLM/LRM marks (which nudge rather than isolate, and have to be
 * placed by hand at each boundary) and over embeddings (which nest but do not
 * isolate, so a stray unterminated one corrupts the rest of the document).
 */
export const isolate = (s: string) => (s ? `⁨${s}⁩` : s)

/**
 * Open a Hebrew line right to left.
 *
 * Isolates are skipped when a line's direction is decided, and every piece of
 * a line here is isolated — so a line has no strong character of its own, and
 * a reader that decides direction per line (a phone, a text-only mail client,
 * the preview) sets every one of them left to right. A timed line then reads
 * its label and time backwards, and the shkia line, which is one isolate, the
 * other way round from its neighbors.
 *
 * A right-to-left mark (U+200F) is a strong character nobody sees: placed
 * first, it decides the line, so label, colon and time sit the way the sheet
 * sets them. A line whose first letter is not Hebrew is left as it is.
 */
const directed = (line: string) => {
  const letter = line.match(/\p{L}/u)?.[0]
  return letter && /\p{Script=Hebrew}/u.test(letter) ? `‏${line}` : line
}

/**
 * `8:45 AM` → `8:45a`, for a rendering that cannot style.
 *
 * The printed sheet keeps the meridiem and sets it small, because the hour is
 * the fact and the meridiem only qualifies it. Plain text has no small — so a
 * text message either shouts it or drops it. Dropping is not safe: most lines
 * are unambiguous from the tefilla's name, but Selichos at 12:45 and a fast
 * beginning at alos 4:12 are not, and a member who reads one wrong misses the
 * minyan. A single letter keeps the distinction at a third of the weight.
 *
 * Global, so a composite line carrying two opinions has both compacted.
 */
const compactMeridiem = (s: string) =>
  s.replace(
    /(\d{1,2}:\d{2})\s*([AP])M\b/gi,
    (_m, time, ap) => `${time}${ap.toLowerCase()}`,
  )

/**
 * The printed value of a line as plain text — a composite joins its parts.
 *
 * Exported because a token naming ONE time renders through this same function
 * that renders the line inside a block. Two formatters would be two answers to
 * "what does this line say", and the sentence and the table would eventually
 * disagree about a figure they both took from one resolve.
 */
export function timeText(e: LuachEntry): string {
  if (e.parts?.length) {
    return e.parts
      .map((p) => `${plain(p.labelHe)} ${p.time ?? ''}`.trim())
      .join(' / ')
  }
  return e.displayText ?? e.time ?? e.noteText ?? ''
}

/**
 * Render a resolved occasion as message text — the weekly-zmanim broadcast body.
 * The email carries the full schedule (the same times the luach prints); the SMS
 * is a short teaser (shul, occasion, candle-lighting) that the caller finishes
 * with a link to the public page. Kept beside the luach so a message and the
 * sheet never disagree about the times.
 *
 * `segment` narrows a multi-day chag to one of its parts (first days, chol
 * hamoed, last days). The days are the SAME resolved days either way — a
 * segment is a cut, not a second computation — so a first-days message and the
 * printed sheet cannot disagree about the times they share.
 */
export function scheduleToText(
  result: LuachResult,
  shul: LuachShul,
  segment?: ScheduleSegmentKey,
  options: {
    /** Render `8:45a` rather than `8:45 AM` — for SMS, which cannot style. */
    compactMeridiem?: boolean
  } = {},
): { subject: string; body: string; sms: string } {
  const time = (v: string) => (options.compactMeridiem ? compactMeridiem(v) : v)
  const name = plain(shul.nameHe || shul.name)
  const days: LuachDay[] = scheduledDays(
    segment ? segmentDays(result, segment) : result.days,
  )
  // A segment names the part it is, so a first-days message does not head
  // itself "Sukkos" and then print two days of it.
  const occasion = segment
    ? `${plain(sheetTitleHe(result))} — ${SEGMENT_LABEL[segment].he}`
    : plain(sheetTitleHe(result))

  const lines: string[] = [isolate(name), '', isolate(occasion)]
  let candle = ''
  for (const day of days) {
    // The heading is the worst offender: a day name and a date around a
    // neutral separator, inside neutral dashes. Isolating each side keeps one
    // from being pulled into the other's run. The date is the Hebrew form, so
    // a Hebrew heading does not switch direction mid-line; the printed sheet
    // keeps the English rendering, and that divergence lives in the two
    // fields, not in a flag.
    lines.push(
      '',
      `— ${isolate(plain(day.dayLabelHe))} · ${isolate(day.hebrewDateHe)} —`,
    )
    for (const item of withSunset(day)) {
      // שקיעה, among the times it explains — one phrase, no colon, because it
      // is a reference rather than a davening. `withSunset` is what knows to
      // place it; walking `day.entries` alone leaves it out entirely.
      if (item.kind === 'anchor') {
        lines.push(isolate(`שקיעה ${time(item.time)}`))
        continue
      }
      const e = item.entry
      if (e.emphasis === 'hidden') continue
      // A note is its own sentence on its own line — a "label: value" pair
      // would print the same words on both sides of the colon.
      if (isNoteLine(e)) {
        lines.push(isolate(plain(e.noteText ?? '')))
        continue
      }
      const t = timeText(e)
      const label = labelWithPlace(e)
      if (!t && !e.noteText) continue
      // Label and time are isolated separately so the colon between them stays
      // where it was written instead of migrating to the end of the line.
      lines.push(t ? `${isolate(label)}: ${isolate(time(t))}` : isolate(label))
      if (!candle && /נרות/.test(label) && e.time) candle = e.time
    }
  }
  if (result.greetingHe) lines.push('', isolate(plain(result.greetingHe)))

  const sms = [
    `${isolate(name)} — ${isolate(occasion)}`,
    candle ? `Candle lighting ${isolate(time(candle))}` : '',
  ]
    .filter(Boolean)
    .map(directed)
    .join('\n')

  return {
    // The subject line is chrome in most clients and some strip the isolates,
    // so it keeps the plain form; the body is where the tangling was visible.
    subject: `${name} — ${occasion}`,
    body: lines.map(directed).join('\n'),
    sms,
  }
}
